import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getEmployeesList,
    deleteEmployee,
    updateEmployee,
    getEmployeeAuditLog,
    getEmployeeTypes,
    searchEmployeeByEmail,
    clearEmployeeSearch,
    linkExistingEmployee,
} from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { getShiftAssignments } from '../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { readableTextOn, shiftTimeRange, todayISO } from '../../../consts/shiftConstants';
import Pagination from '../../../components/common/Pagination';
import BulkUploadModal from '../Society/WingsAndFlats/BulkUploadModal';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import { notify } from '../../../utils/notification';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_EMPLOYEES_ADD,
    SOCIETY_PORTAL_EMPLOYEES_EDIT,
    SOCIETY_PORTAL_EMPLOYEES_VIEW,
    SOCIETY_PORTAL_EMPLOYEES_WORK_LOG,
    SOCIETY_PORTAL_EMPLOYEE_COMPLAINTS,
} from '../../../Routing/routes';
import { formatIstDateTime } from '../../../utils/helpers/istDate';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

const ACTION_STYLE = {
    CREATE: 'tw-bg-emerald-100 tw-text-emerald-700',
    UPDATE: 'tw-bg-amber-100 tw-text-amber-700',
    DELETE: 'tw-bg-rose-100 tw-text-rose-700',
};

// Whitelist of fields to display in the audit diff (ordered)
const AUDIT_FIELDS = [
    'first_name',
    'last_name',
    'username',
    'email',
    'phone',
    'address',
    'joining_date',
    'employment_type',
];

const AUDIT_FIELD_LABELS = {
    first_name: 'First Name',
    last_name: 'Last Name',
    username: 'Username',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    joining_date: 'Joining Date',
    employment_type: 'Employment Type',
};

const formatAuditValue = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

// Audit log values arrive from a JSON column that Sequelize normally parses,
// but old rows (or rows written before the column was JSON-typed) can come
// back as JSON strings. `key in <string>` throws — which crashes the modal
// and blanks the page. Coerce to an object first, defensively.
const safeJson = (v) => {
    if (v == null) return null;
    if (typeof v === 'object') return v;
    if (typeof v === 'string') {
        try { return JSON.parse(v); } catch (_) { return null; }
    }
    return null;
};

const buildAuditDiff = ({ action, old_values, new_values, changed_fields }) => {
    const upper = (action || '').toUpperCase();
    const oldObj = safeJson(old_values);
    const newObj = safeJson(new_values);

    if (upper === 'CREATE' && newObj) {
        return AUDIT_FIELDS
            .filter(key => key in newObj)
            .map(key => ({ key, old: null, new: newObj[key] }));
    }
    if (upper === 'DELETE' && oldObj) {
        return AUDIT_FIELDS
            .filter(key => key in oldObj)
            .map(key => ({ key, old: oldObj[key], new: null }));
    }
    // UPDATE — prefer the explicit list from the backend
    if (Array.isArray(changed_fields) && changed_fields.length > 0) {
        return AUDIT_FIELDS
            .filter(key => changed_fields.includes(key))
            .map(key => ({
                key,
                old: oldObj?.[key] ?? null,
                new: newObj?.[key] ?? null,
            }));
    }
    // Fallback: diff whitelisted keys where the value actually changed
    return AUDIT_FIELDS
        .map(key => ({ key, old: oldObj?.[key] ?? null, new: newObj?.[key] ?? null }))
        .filter(({ old, new: nv }) => JSON.stringify(old) !== JSON.stringify(nv));
};

const AuditEntry = ({ log }) => {
    const [expanded, setExpanded] = useState(false);

    const action = (log.action || '').toUpperCase();
    const when = log.created_at || log.timestamp || log.when || log.date;
    const whenFmt = when ? formatIstDateTime(when) : '—';
    const by = log.user
        ? ([log.user.first_name, log.user.last_name].filter(Boolean).join(' ').trim() || log.user.email || '-')
        : (log.by || log.performed_by || '-');

    const diff = buildAuditDiff(log);
    const actionClass = ACTION_STYLE[action] || 'tw-bg-purple-100 tw-text-purple-700';

    return (
        <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 tw-mb-3 hover:tw-border-gray-200 tw-transition-colors">
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-1.5">
                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-rounded-full ${actionClass}`}>
                    {action || 'EVENT'}
                </span>
                <span className="tw-text-[11px] tw-text-gray-600">{whenFmt}</span>
                <span className="tw-text-[11px] tw-text-gray-400">·</span>
                <span className="tw-text-[11px] tw-text-gray-700">by {by}</span>
                {log.ip_address && (
                    <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono tw-ml-auto">IP {log.ip_address}</span>
                )}
            </div>

            {log.description && (
                <p className="tw-text-xs tw-text-gray-700 tw-mb-1.5">{log.description}</p>
            )}

            {(log.request_method || log.request_url) && (
                <p className="tw-text-[10px] tw-text-gray-400 tw-font-mono tw-mb-2">
                    {log.request_method} {log.request_url}
                </p>
            )}

            {diff.length > 0 && (
                <button
                    onClick={() => setExpanded(s => !s)}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-blue-600 hover:tw-text-blue-800"
                >
                    <Icon icon={expanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} />
                    {expanded ? 'Hide' : 'Show'} {diff.length} {diff.length === 1 ? 'change' : 'changes'}
                </button>
            )}

            {expanded && diff.length > 0 && (
                <div className="tw-mt-2 tw-border-t tw-border-gray-100 tw-pt-2 tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-[10px]">
                        <thead>
                            <tr className="tw-text-gray-400 tw-uppercase tw-tracking-wide">
                                <th className="tw-text-left tw-py-1.5 tw-pr-3 tw-font-bold">Field</th>
                                <th className="tw-text-left tw-py-1.5 tw-pr-3 tw-font-bold">Before</th>
                                <th className="tw-text-left tw-py-1.5 tw-font-bold">After</th>
                            </tr>
                        </thead>
                        <tbody>
                            {diff.map(({ key, old, new: nv }) => (
                                <tr key={key} className="tw-border-t tw-border-gray-50">
                                    <td className="tw-py-1.5 tw-pr-3 tw-text-gray-700 tw-font-semibold tw-whitespace-nowrap">
                                        {AUDIT_FIELD_LABELS[key] || key}
                                    </td>
                                    <td className="tw-py-1.5 tw-pr-3 tw-font-mono tw-text-rose-700 tw-break-all">
                                        {formatAuditValue(old)}
                                    </td>
                                    <td className="tw-py-1.5 tw-font-mono tw-text-emerald-700 tw-break-all">
                                        {formatAuditValue(nv)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// Generic modal shell — used by Audit / Add-from-existing / Login-report dialogs
const Modal = ({ title, subtitle, icon, onClose, children, size = 'md' }) => {
    const widthClass = size === 'lg' ? 'tw-max-w-3xl' : size === 'sm' ? 'tw-max-w-sm' : 'tw-max-w-xl';
    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className={`tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full ${widthClass} tw-overflow-hidden`}>
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-start tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        {icon && (
                            <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center">
                                <Icon icon={icon} className="tw-text-lg" />
                            </div>
                        )}
                        <div>
                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">{title}</h3>
                            {subtitle && <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subtitle}</p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// Excel template for the bulk upload. Column names are the API field names, so
// the sheet maps 1:1 onto a single Add Employee request. Required: first_name,
// phone, email (every employee gets a login), employee_type. The last 13 are the bank & personal details.
// Fallback sheet, if the server template cannot be fetched. The real template
// comes from GET /employees/bulk-template, which adds dropdowns for the coded
// columns. Full name and user name mirror the Add Employee form; there is no
// employee_code column because the system generates it.
const EMPLOYEE_UPLOAD_HEADERS = [
    'full_name', 'user_name', 'phone', 'email', 'employee_type', 'joining_date', 'address',
    'access_role', 'password', 'shift', 'shift_effective_from', 'weekly_off_days', 'designation', 'department', 'employment_type', 'date_of_birth',
    'gender', 'salary', 'alternate_phone',
    'emergency_contact_name', 'emergency_contact_phone', 'id_proof_type', 'id_proof_number',
    'marital_status', 'blood_group', 'nationality', 'qualification', 'pan_number', 'aadhar_number',
    'bank_account_holder_name', 'bank_name', 'bank_account_number', 'bank_ifsc_code',
    'bank_branch_name', 'bank_account_type', 'upi_id',
];
const EMPLOYEE_UPLOAD_SAMPLE = [
    'Ramesh Patil', 'ramesh.patil', '9876543210', 'ramesh@example.com', 'Security', '2026-04-01', 'Gate 1, Tower A',
    'General Employee', '', 'General', '', 'SUN', 'Guard', 'SECURITY', 'PERMANENT', '1990-05-12',
    'MALE', 15000, '',
    'Sunita Patil', '9876500000', 'AADHAR', '123412341234',
    'MARRIED', 'B+', 'Indian', 'HSC', 'ABCDE1234F', '123412341234',
    'Ramesh Patil', 'HDFC Bank', '50100012345678', 'HDFC0001234',
    'Andheri East', 'SAVINGS', 'ramesh@okhdfc',
];
const EMPLOYEE_UPLOAD_NOTES = [
    'Required', 'Required — the login user name', 'Required, 10 digits', 'Required — used as the employee login',
    'Required — e.g. Security, Housekeeping, Maintenance, Manager (as shown in the app)', 'Required — YYYY-MM-DD', 'Required',
    'Recommended — e.g. General Employee, Accountant, Society Administrator',
    'Optional, min 6 characters — blank sets the default password', 'Optional — shift name as shown under Shifts',
    'Optional — YYYY-MM-DD, blank means today', 'Optional — days off, e.g. SAT, SUN (needs a shift)', 'Optional',
    'SECURITY / MAINTENANCE / HOUSEKEEPING / ADMINISTRATION / ACCOUNTS / MANAGEMENT / OTHER',
    'PERMANENT / CONTRACT / TEMPORARY / PART_TIME', 'YYYY-MM-DD',
    'MALE / FEMALE / OTHER', 'Number', 'Optional',
    'Optional', 'Optional', 'AADHAR / PAN / VOTER_ID / DRIVING_LICENSE / PASSPORT / OTHER', 'Optional',
    'SINGLE / MARRIED / DIVORCED / WIDOWED', 'A+ A- B+ B- AB+ AB- O+ O-', 'Optional', 'Optional', 'ABCDE1234F format', '12 digits',
    'Optional', 'Optional', '6–30 digits', 'e.g. HDFC0001234',
    'Optional', 'SAVINGS / CURRENT / SALARY', 'Optional',
];

// The template with dropdowns is built by the API; this fetches and saves it.
const downloadEmployeeTemplate = async (societyId) => {
    const res = await axiosInstance.get(apiEndpoints.SOCIETY_EMPLOYEE_UPLOAD_TEMPLATE_API, {
        params: { society_id: societyId },
        responseType: 'blob'
    });
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees-upload-template.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
};

const Employees = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { can } = usePermissions();
    const canReadShifts = can(PERMISSIONS.SHIFT_READ);
    const canViewWorkLog = can(PERMISSIONS.EMPLOYEE_WORK_LOG);

    const {
        employeesListData,
        employeesListLoading,
        employeesPagination,
        deleteEmployeeLoading,
        updateEmployeeLoading,
        employeeAuditLogData,
        employeeAuditLogLoading,
        employeeTypesData,
        employeeTypesLoading,
        searchedEmployeeData,
        searchedEmployeeLoading,
        searchedEmployeeError,
        linkEmployeeLoading,
    } = useSelector(state => state.EmployeeReducer || {});

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    const [deleteTarget, setDeleteTarget] = useState(null);
    // Bulk upload: modal visibility, request in flight, and the server's
    // { success, message, data: { inserted, failed, errors[] } } for the report.
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [auditTarget, setAuditTarget] = useState(null);
    const [addExistingOpen, setAddExistingOpen] = useState(false);

    // Add From Existing modal state
    const [searchEmail, setSearchEmail] = useState('');
    const [selectedTypeId, setSelectedTypeId] = useState('');

    // Prefill dropdown with the matched employee's current type
    useEffect(() => {
        if (searchedEmployeeData) {
            const currentTypeId = searchedEmployeeData.employee_type_id
                ?? searchedEmployeeData.employeeType?.employee_type_id;
            if (currentTypeId != null) setSelectedTypeId(String(currentTypeId));
        }
    }, [searchedEmployeeData]);

    const fetchList = useCallback(() => {
        if (!societyId) return;
        dispatch(getEmployeesList({
            societyId,
            page,
            limit: itemsPerPage,
            search: search || undefined,
            status: status === 'all' ? undefined : status,
        }));
    }, [dispatch, societyId, page, search, status]);
    usePageRefresh(fetchList);

    const handleBulkUpload = async (file) => {
        setBulkUploading(true);
        setBulkResult(null);
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('society_id', societyId);
            const res = await axiosInstance.post(apiEndpoints.CTRL_EMPLOYEE_BULK_UPLOAD_API, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setBulkResult(res);
            // Show the new rows straight away; the report stays open on top.
            if (res?.data?.inserted) fetchList();
        } catch (err) {
            setBulkResult({
                success: false,
                message: err?.response?.data?.message || err?.message || 'Bulk upload failed',
            });
        } finally {
            setBulkUploading(false);
        }
    };

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
    const handleStatusChange = (e) => { setStatus(e.target.value); setPage(1); };

    const [statusTarget, setStatusTarget] = useState(null);

    const handleToggleStatus = () => {
        if (!statusTarget) return;
        dispatch(updateEmployee({
            id: statusTarget.id,
            is_active: !statusTarget.isActive,
            onSuccess: () => { setStatusTarget(null); fetchList(); },
        }));
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteEmployee({
            id: deleteTarget.id,
            onSuccess: () => { setDeleteTarget(null); fetchList(); },
        }));
    };

    const openAddExisting = () => {
        setAddExistingOpen(true);
        setSearchEmail('');
        setSelectedTypeId('');
        dispatch(clearEmployeeSearch());
        if (!employeeTypesData) dispatch(getEmployeeTypes({ societyId }));
    };

    const closeAddExisting = () => {
        setAddExistingOpen(false);
        setSearchEmail('');
        setSelectedTypeId('');
        dispatch(clearEmployeeSearch());
    };

    const handleSearchExisting = (e) => {
        e?.preventDefault?.();
        const email = searchEmail.trim();
        if (!email) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        dispatch(searchEmployeeByEmail({ email, societyId }));
    };

    const handleLinkExisting = () => {
        if (!searchedEmployeeData || !selectedTypeId) return;
        const userId = searchedEmployeeData.user_id ?? searchedEmployeeData.user?.user_id;
        const employeeId = searchedEmployeeData.employee_id;
        dispatch(linkExistingEmployee({
            society_id: societyId,
            employee_type_id: selectedTypeId,
            email: searchEmail.trim(),
            user_id: userId,
            employee_id: employeeId,
            onSuccess: () => { closeAddExisting(); fetchList(); },
        }));
    };

    const existingFullName = searchedEmployeeData
        ? ([searchedEmployeeData.first_name, searchedEmployeeData.last_name].filter(Boolean).join(' ').trim()
            || searchedEmployeeData.name || '-')
        : '';
    const employeeTypeOptions = (Array.isArray(employeeTypesData) ? employeeTypesData : [])
        .map(t => ({
            value: t.employee_type_id ?? t.id ?? t.value,
            label: t.type ?? t.name ?? t.label,
        }))
        .filter(o => o.value && o.label);

    const employees = Array.isArray(employeesListData) ? employeesListData : [];

    // Only the single-employee responses carry `shift_assignment`; the list
    // endpoints do not. So today's assignments are fetched once — active_on
    // matches open-ended ones too, which is nearly all of them — and joined on
    // employee_id. The 100-row cap matches the endpoint's max.
    const [shiftByEmployee, setShiftByEmployee] = useState({});

    useEffect(() => {
        if (!canReadShifts) return;
        dispatch(getShiftAssignments({
            active_on: todayISO(),
            is_active: true,
            limit: 100,
            onSuccess: (rows) => {
                const byEmployee = {};
                (rows || []).forEach((a) => { byEmployee[a.employee_id] = a; });
                setShiftByEmployee(byEmployee);
            },
        }));
    }, [dispatch, canReadShifts]);

    const totalPages = employeesPagination?.totalPages || 1;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                {/* Header */}
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Employees</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Manage society employees</p>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {canViewWorkLog && (
                            <button
                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES_WORK_LOG)}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-indigo-700 tw-border tw-border-indigo-200 tw-bg-indigo-50 hover:tw-bg-indigo-100 tw-transition-colors"
                            >
                                <Icon icon="mdi:timeline-clock-outline" className="tw-text-base" />
                                Work Log
                            </button>
                        )}
                        <button
                            onClick={openAddExisting}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 tw-transition-colors"
                        >
                            <Icon icon="mdi:account-search-outline" className="tw-text-base" />
                            Add From Existing Employee
                        </button>
                        <button
                            onClick={() => { setBulkResult(null); setShowBulkUpload(true); }}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-bg-emerald-50 hover:tw-bg-emerald-100 tw-transition-colors"
                        >
                            <Icon icon="mdi:file-excel-outline" className="tw-text-base" />
                            Upload Excel
                        </button>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES_ADD)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:plus" className="tw-text-base" />
                            Add Employee
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-relative tw-flex-1 tw-min-w-[180px] sm:tw-max-w-xs">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-sm tw-text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone..."
                            value={search}
                            onChange={handleSearchChange}
                            className="tw-w-full tw-pl-8 tw-pr-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    <select
                        value={status}
                        onChange={handleStatusChange}
                        className="tw-w-auto tw-min-w-[120px] tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Table */}
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Name</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Type</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Phone Number</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Email</th>
                                {canReadShifts && (
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Shift</th>
                                )}
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Status</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeesListLoading && (
                                <tr>
                                    <td colSpan={canReadShifts ? 7 : 6} className="tw-text-center tw-py-12">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                            <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!employeesListLoading && employees.length === 0 && (
                                <tr>
                                    <td colSpan={canReadShifts ? 7 : 6} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                        No employees found
                                    </td>
                                </tr>
                            )}
                            {!employeesListLoading && employees.map((emp) => {
                                const empId = emp.employee_id ?? emp.id;
                                const fullName = emp.name
                                    || [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim()
                                    || '-';
                                const typeLabel = emp.employeeType?.type
                                    || emp.type
                                    || emp.employee_type
                                    || '-';
                                const isActive = emp.is_active === 1 || emp.is_active === true
                                    || (emp.status || '').toLowerCase() === 'active';
                                const statusLabel = isActive ? 'Active' : 'Inactive';
                                return (
                                    <tr key={empId} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50 tw-transition-colors">
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-font-medium">{fullName}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{typeLabel}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{emp.phone || '-'}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{emp.email || '-'}</td>
                                        {canReadShifts && (
                                            <td className="tw-px-4 tw-py-2.5">
                                                {shiftByEmployee[empId] ? (
                                                    <span
                                                        className="tw-inline-block tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold"
                                                        style={{
                                                            backgroundColor: shiftByEmployee[empId].shift?.color_hex || '#E5E7EB',
                                                            color: readableTextOn(shiftByEmployee[empId].shift?.color_hex),
                                                        }}
                                                        title={shiftTimeRange(shiftByEmployee[empId].shift)}
                                                    >
                                                        {shiftByEmployee[empId].shift?.shift_code || '—'}
                                                    </span>
                                                ) : (
                                                    <span className="tw-text-[11px] tw-text-gray-300">—</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${isActive ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {statusLabel}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                            <div className="tw-inline-flex tw-items-center tw-gap-1">
                                                <button
                                                    title="View"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES_VIEW.replace(':id', empId))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600"
                                                >
                                                    <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Edit"
                                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES_EDIT.replace(':id', empId))}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                                >
                                                    <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Audit Log"
                                                    onClick={() => {
                                                        setAuditTarget({ ...emp, id: empId, name: fullName });
                                                        dispatch(getEmployeeAuditLog({ id: empId, societyId }));
                                                    }}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-purple-50 tw-text-purple-600"
                                                >
                                                    <Icon icon="mdi:history" className="tw-text-base" />
                                                </button>
                                                {canViewWorkLog && (
                                                    <button
                                                        title="Work Log"
                                                        onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_EMPLOYEES_WORK_LOG}?employeeId=${empId}`)}
                                                        className="tw-p-1.5 tw-rounded hover:tw-bg-indigo-50 tw-text-indigo-600"
                                                    >
                                                        <Icon icon="mdi:timeline-clock-outline" className="tw-text-base" />
                                                    </button>
                                                )}
                                                {(emp.user_id || emp.user?.user_id) && (
                                                    <button
                                                        title="Complaint History"
                                                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEE_COMPLAINTS.replace(':userId', emp.user_id || emp.user?.user_id))}
                                                        className="tw-p-1.5 tw-rounded hover:tw-bg-teal-50 tw-text-teal-600"
                                                    >
                                                        <Icon icon="mdi:message-text-outline" className="tw-text-base" />
                                                    </button>
                                                )}
                                                <button
                                                    title={isActive ? 'Deactivate employee' : 'Activate employee'}
                                                    onClick={() => setStatusTarget({ id: empId, name: fullName, isActive })}
                                                    className={`tw-p-1.5 tw-rounded ${isActive ? 'hover:tw-bg-amber-50 tw-text-amber-600' : 'hover:tw-bg-emerald-50 tw-text-emerald-600'}`}
                                                >
                                                    <Icon icon={isActive ? 'mdi:account-cancel-outline' : 'mdi:account-check-outline'} className="tw-text-base" />
                                                </button>
                                                <button
                                                    title="Delete"
                                                    onClick={() => setDeleteTarget({ ...emp, id: empId, name: fullName })}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!employeesListLoading && employees.length > 0 && totalPages > 1 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                )}
            </div>

            {/* Activate / deactivate confirmation. Deactivating is not a delete: the
                record and its attendance history stay, the person just stops appearing
                as active staff and can no longer be assigned work. */}
            {statusTarget && (
                <Modal
                    title={statusTarget.isActive ? 'Deactivate Employee?' : 'Activate Employee?'}
                    subtitle={statusTarget.isActive ? 'They will no longer count as active staff.' : 'They will count as active staff again.'}
                    icon={statusTarget.isActive ? 'mdi:account-cancel-outline' : 'mdi:account-check-outline'}
                    size="sm"
                    onClose={() => setStatusTarget(null)}
                >
                    <div className="tw-p-5">
                        <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                            {statusTarget.isActive ? 'Deactivate' : 'Activate'} <span className="tw-font-semibold">{statusTarget.name}</span>?
                            {statusTarget.isActive && ' Their record and attendance history are kept — this can be undone at any time.'}
                        </p>
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button onClick={() => setStatusTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                            <button
                                onClick={handleToggleStatus}
                                disabled={updateEmployeeLoading}
                                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-rounded-md disabled:tw-opacity-50 ${statusTarget.isActive ? 'tw-bg-amber-600 hover:tw-bg-amber-700' : 'tw-bg-emerald-600 hover:tw-bg-emerald-700'}`}
                            >
                                {updateEmployeeLoading ? 'Saving...' : (statusTarget.isActive ? 'Deactivate' : 'Activate')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete confirmation */}
            {deleteTarget && (
                <Modal
                    title="Delete Employee?"
                    subtitle="This action cannot be undone."
                    icon="mdi:alert-outline"
                    size="sm"
                    onClose={() => setDeleteTarget(null)}
                >
                    <div className="tw-p-5">
                        <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                            You are about to delete <span className="tw-font-semibold">{deleteTarget.name}</span>.
                        </p>
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                            <button onClick={handleDelete} disabled={deleteEmployeeLoading} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50">
                                {deleteEmployeeLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Audit Log modal */}
            {auditTarget && (
                <Modal
                    title={`Audit Log — ${auditTarget.name}`}
                    subtitle="Recent activity on this employee record"
                    icon="mdi:history"
                    size="lg"
                    onClose={() => setAuditTarget(null)}
                >
                    <div className="tw-p-5 tw-max-h-[70vh] tw-overflow-y-auto">
                        {employeeAuditLogLoading && (
                            <div className="tw-flex tw-justify-center tw-py-12">
                                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-purple-500" />
                            </div>
                        )}
                        {!employeeAuditLogLoading && (!employeeAuditLogData || employeeAuditLogData.length === 0) && (
                            <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                No audit entries found
                            </div>
                        )}
                        {!employeeAuditLogLoading && (employeeAuditLogData || []).map((log, i) => (
                            <AuditEntry key={log.audit_id ?? log.id ?? i} log={log} />
                        ))}
                    </div>
                </Modal>
            )}

            {/* Add From Existing Employee modal */}
            {addExistingOpen && (
                <Modal
                    title="Add From Existing Employee"
                    subtitle="Find an existing employee by email and assign a type for this society"
                    icon="mdi:account-search-outline"
                    onClose={closeAddExisting}
                >
                    <div className="tw-p-5">
                        {/* Email search */}
                        <form onSubmit={handleSearchExisting} className="tw-flex tw-gap-2 tw-mb-4">
                            <div className="tw-relative tw-flex-1">
                                <Icon icon="mdi:email-search-outline" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                                <input
                                    type="email"
                                    value={searchEmail}
                                    onChange={e => setSearchEmail(e.target.value)}
                                    placeholder="Search by email..."
                                    className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={searchedEmployeeLoading || !searchEmail.trim()}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                {searchedEmployeeLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                Search
                            </button>
                        </form>

                        {/* Result / error / empty */}
                        {searchedEmployeeError && !searchedEmployeeLoading && (
                            <div className="tw-px-3 tw-py-2 tw-text-xs tw-text-rose-700 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-lg tw-mb-3">
                                <Icon icon="mdi:alert-circle-outline" className="tw-inline tw-text-sm tw-mr-1.5 tw--mt-0.5" />
                                {searchedEmployeeError}
                            </div>
                        )}
                        {!searchedEmployeeLoading && !searchedEmployeeError && !searchedEmployeeData && (
                            <div className="tw-text-center tw-py-8 tw-text-gray-400 tw-text-xs">
                                Enter an email and click <span className="tw-font-semibold">Search</span> to find an existing employee.
                            </div>
                        )}

                        {searchedEmployeeData && (
                            <div className="tw-space-y-4">
                                {/* Matched employee summary */}
                                <div className="tw-p-3 tw-bg-emerald-50 tw-border tw-border-emerald-100 tw-rounded-lg tw-flex tw-items-center tw-gap-3">
                                    <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-emerald-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold">
                                        {existingFullName.charAt(0).toUpperCase() || '?'}
                                    </div>
                                    <div className="tw-flex-1 tw-min-w-0">
                                        <p className="tw-text-xs tw-font-bold tw-text-gray-800">{existingFullName}</p>
                                        <p className="tw-text-[11px] tw-text-gray-600">{searchedEmployeeData.email}</p>
                                        {searchedEmployeeData.phone && (
                                            <p className="tw-text-[11px] tw-text-gray-500 tw-font-mono">{searchedEmployeeData.phone}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Employee type dropdown for this society */}
                                <div>
                                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                                        Employee Type <span className="tw-text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={selectedTypeId}
                                        onChange={e => setSelectedTypeId(e.target.value)}
                                        disabled={employeeTypesLoading}
                                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    >
                                        <option value="">{employeeTypesLoading ? 'Loading…' : 'Select Employee Type'}</option>
                                        {employeeTypeOptions.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    {searchedEmployeeData?.employeeType?.type && (
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                                            Currently: <span className="tw-font-semibold tw-text-gray-600">{searchedEmployeeData.employeeType.type}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
                            <button
                                onClick={closeAddExisting}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLinkExisting}
                                disabled={linkEmployeeLoading || !searchedEmployeeData || !selectedTypeId}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                {linkEmployeeLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                Save
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showBulkUpload && (
                <BulkUploadModal
                    title="Bulk Upload Employees"
                    description="Add many employees at once. Each row is created exactly like Add Employee — including bank & personal details and a login account, so every row needs an email."
                    templateName="employees_upload_template.xlsx"
                    templateHeaders={EMPLOYEE_UPLOAD_HEADERS}
                    templateSample={EMPLOYEE_UPLOAD_SAMPLE}
                    templateNotes={EMPLOYEE_UPLOAD_NOTES}
                    onDownloadTemplate={() => downloadEmployeeTemplate(societyId).catch(() => {
                        // Server unreachable: fall back to the plain sheet built here.
                        notify('error', 'Could not fetch the template with dropdowns — downloading a plain sheet instead.');
                        const rows = [EMPLOYEE_UPLOAD_HEADERS, EMPLOYEE_UPLOAD_SAMPLE, EMPLOYEE_UPLOAD_NOTES];
                        const ws = XLSX.utils.aoa_to_sheet(rows);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Employees');
                        XLSX.writeFile(wb, 'employees_upload_template.xlsx');
                    })}
                    onClose={() => { setShowBulkUpload(false); setBulkResult(null); }}
                    onUpload={handleBulkUpload}
                    uploading={bulkUploading}
                    result={bulkResult}
                />
            )}

        </div>
    );
};

export default Employees;
